import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../utils/theme';
import './TeacherSidebar.css';

const TeacherSidebar = ({ activeSection, setActiveSection, isOpen = false, onClose }) => {
  const { t } = useTranslation('teacher');
  const { isDark, toggleTheme } = useTheme();
  const [profileImage, setProfileImage] = useState('/default-avatar.png');
  
  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // UX Best Practice: Display full name (first_name + last_name)
  // Properly capitalize: First letter uppercase, rest lowercase
  const capitalizeName = (name) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };
  
  const getUserDisplayName = () => {
    if (user.first_name && user.last_name) {
      // Display full name: "Soufiane Rihani"
      return `${capitalizeName(user.first_name)} ${capitalizeName(user.last_name)}`.trim();
    }
    if (user.first_name) {
      // Only first name available
      return capitalizeName(user.first_name);
    }
    if (user.name) {
      // Use full name from name field
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return `${capitalizeName(nameParts[0])} ${capitalizeName(nameParts.slice(1).join(' '))}`.trim();
      }
      return capitalizeName(nameParts[0]);
    }
    if (user.email) {
      return capitalizeName(user.email.split('@')[0]);
    }
    return 'Teacher';
  };
  
  const getUserRole = () => {
    if (user.role) {
      return user.role.charAt(0).toUpperCase() + user.role.slice(1);
    }
    return 'Teacher';
  };
  
  const getUserInitials = () => {
    if (user.first_name && user.last_name) {
      return (user.first_name[0] + user.last_name[0]).toUpperCase();
    }
    if (user.first_name) {
      return user.first_name.substring(0, 2).toUpperCase();
    }
    if (user.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'TS';
  };
  
  const displayName = getUserDisplayName();
  const displayRole = getUserRole();
  const userInitials = getUserInitials();

  const navSections = [
    {
      title: t('sidebar.dashboard'),
      items: [
        { id: 'dashboard', label: t('sidebar.dashboard'), icon: 'home', badge: null }
      ]
    },
    {
      title: t('sidebar.teaching'),
      items: [
        { id: 'my-students', label: t('sidebar.myStudents'), icon: 'groups', badge: null },
        { id: 'courses', label: t('sidebar.myCourses'), icon: 'menu_book', badge: null },
        { id: 'live-classes', label: t('sidebar.liveClasses'), icon: 'video_call', badge: null },
        { id: 'course-library-management', label: t('sidebar.courseLibraryManagement'), icon: 'library_books', badge: null },
        { id: 'add-article', label: t('sidebar.addArticle'), icon: 'edit', badge: null },
        { id: 'my-articles', label: t('sidebar.myArticles'), icon: 'article', badge: null },
        { id: 'vocabulary', label: t('sidebar.myVocabulary'), icon: 'library_books', badge: null }
      ]
    },
    {
      title: t('sidebar.communication'),
      items: [
        { id: 'student-chat', label: t('sidebar.studentChat'), icon: 'chat', badge: null },
        { id: 'notifications', label: t('sidebar.notifications'), icon: 'notifications', badge: 3 }
      ]
    },
    {
      title: t('sidebar.learning'),
      items: [
        { id: 'explore-courses', label: t('sidebar.exploreCourses'), icon: 'school', badge: null },
        { id: 'my-learning-courses', label: t('sidebar.myLearningCourses'), icon: 'book', badge: null },
        { id: 'live-sessions', label: t('sidebar.liveSessions'), icon: 'video_camera_front', badge: null }
      ]
    },
    {
      title: t('sidebar.resources'),
      items: [
        { id: 'course-library', label: t('sidebar.courseLibrary'), icon: 'library_books', badge: null },
        { id: 'shop', label: t('sidebar.shop'), icon: 'shopping_cart', badge: null },
        { id: 'free-talk', label: t('sidebar.freeTalk'), icon: 'forum', badge: null }
      ]
    },
    {
      title: t('sidebar.business'),
      items: [
        { id: 'earnings', label: t('sidebar.myEarnings'), icon: 'receipt_long', badge: null }
      ]
    },
    {
      title: t('sidebar.account'),
      items: [
        { id: 'profile-settings', label: t('sidebar.profileSettings'), icon: 'person', badge: null },
        { id: 'account-settings', label: t('sidebar.accountSettings'), icon: 'settings', badge: null },
        { id: 'subscription', label: t('sidebar.subscription', { defaultValue: 'Subscription' }), icon: 'subscriptions', badge: null }
      ]
    }
  ];

  return (
    <div className={`teacher-sidebar ${isOpen ? 'open' : ''}`}>
      {onClose && (
        <button
          type="button"
          className="teacher-sidebar-close-btn"
          onClick={onClose}
          aria-label={t('sidebar.closeMenu')}
        >
          <i className="material-icons">close</i>
        </button>
      )}
      {/* Header */}
      <div className="teacher-sidebar-header">
        <div className="teacher-sidebar-logo">
          <span className="material-icons">school</span>
          <h2>Teacher Panel</h2>
        </div>

        {/* Profile Section */}
        <div className="teacher-profile-section">
          <div className="teacher-profile-picture">
            {profileImage ? (
              <img src={profileImage} alt="Profile" />
            ) : (
              <div className="profile-placeholder">{userInitials}</div>
            )}
            <div className="upload-overlay" onClick={() => document.getElementById('teacher-profile-upload').click()}>
              <i className="material-icons">photo_camera</i>
            </div>
            <input
              id="teacher-profile-upload"
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    setProfileImage(e.target.result);
                  };
                  reader.readAsDataURL(file);
                }
              }}
              style={{ display: 'none' }}
            />
          </div>
          <div className="teacher-profile-info">
            <h3>{displayName}</h3>
            <p>{displayRole}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="teacher-sidebar-nav">
        {navSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="teacher-nav-section">
            <div className="teacher-nav-section-title">{section.title}</div>
            <ul className="teacher-nav-list">
              {section.items.map((item) => (
                <li key={item.id} className="teacher-nav-item">
                  <button
                    className={`teacher-nav-link ${activeSection === item.id ? 'active' : ''}`}
                    onClick={() => setActiveSection(item.id)}
                  >
                    <span className="teacher-nav-icon">
                      <i className="material-icons">{item.icon}</i>
                    </span>
                    <span className="teacher-nav-text">{item.label}</span>
                    {item.badge && (
                      <span className="teacher-nav-badge">{item.badge}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer - Removed menu toggle as sidebar is always visible. Logout moved to TopBar user menu */}
      <div className="teacher-sidebar-footer">
      </div>

      {/* Theme Toggle Button */}
      <button
        type="button"
        className="teacher-theme-toggle"
        onClick={toggleTheme}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-pressed={isDark}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <i className="material-icons">{isDark ? 'light_mode' : 'dark_mode'}</i>
      </button>
    </div>
  );
};

export default TeacherSidebar; 